/**
 * Pure, stateless function that owns ALL three-layer aspect-ratio math for the camera preview.
 *
 * Layer meanings:
 *   L1 — sensor:  the raw camera output (sensorRatio = original width / height, post CSS rotation)
 *   L2 — crop:    app captureRatio setting (cropRatio); undefined means no crop (full L1 frame)
 *   L3 — frame:   frame-slot placeholder (frameRatio); only in Frame mode; drives the dark
 *                 overlay mask in the preview but NEVER changes the saved-photo pixels
 *
 * The function is intentionally pure (no side-effects, no refs, no DOM reads) so it can be
 * called from both Preview.tsx and strip compositor helpers without coupling to React state.
 */

export interface PreviewLayoutInput {
  /** Container size in CSS pixels */
  container: { w: number; h: number }
  /**
   * Real sensor aspect ratio (originalWidth / originalHeight) as returned by the camera.
   * Should be measured AFTER rotation so portrait cameras report < 1.
   * Pass `undefined` until the first camera frame has been decoded.
   */
  sensorRatio: number | undefined
  /**
   * L2 — app captureRatio (width/height).  `undefined` means 'auto' (no crop).
   * Must already be validated (finite, positive) — use `getNormalizedCaptureRatio()` first.
   */
  cropRatio: number | undefined
  /**
   * L3 — frame placeholder aspect (width/height).  `undefined` when not in Frame mode.
   * Only affects the overlay mask, never the saved photo's pixels.
   */
  frameRatio: number | undefined
  /** CSS preview rotation in degrees (0 | 90 | 180 | 270). */
  previewRotation: 0 | 90 | 180 | 270
  /** When true, fill the container edge-to-edge (object-cover behaviour). */
  fillContainer: boolean
  /** previewQuality cap used to size the bitmap (defaults to 1280×720 when omitted). */
  maxBitmapWidth?: number
  maxBitmapHeight?: number
}

export interface PreviewLayoutOutput {
  /** Rotated visual footprint of the preview div in CSS pixels. */
  previewBoxPx: { w: number; h: number }
  /**
   * Pre-rotation canvas backing-store size.
   * For 90/270° this is the SWAPPED (h×w) size so that after CSS rotate the canvas fills previewBoxPx.
   */
  canvasBackingPx: { w: number; h: number }
  /**
   * Dark SVG overlay mask rectangle, or `undefined` when no overlay should be shown.
   * Coordinates are in overlay-div space (same as previewBoxPx when !fillContainer).
   */
  overlayMask?: { x: number; y: number; w: number; h: number; containerW: number; containerH: number }
  /**
   * Crop parameters for `createImageBitmap(blob, sx, sy, sw, sh)` in unrotated sensor space.
   * These represent the L2 crop rectangle.
   */
  cropRectSensor: { sx: number; sy: number; sw: number; sh: number }
  /**
   * Target bitmap size after fitting the crop inside the previewQuality max box while
   * preserving the crop's aspect ratio.  Always even (H.264 friendly).
   */
  bitmapTargetPx: { w: number; h: number }
}

/**
 * Compute all layout values for the camera preview canvas.
 *
 * @param input  See PreviewLayoutInput
 * @param sensorPixelW  Raw sensor width in pixels (from getBitmapParam originalWidth)
 * @param sensorPixelH  Raw sensor height in pixels
 */
export function computePreviewLayout(
  input: PreviewLayoutInput,
  sensorPixelW: number,
  sensorPixelH: number
): PreviewLayoutOutput {
  const {
    container,
    sensorRatio,
    cropRatio,
    frameRatio,
    previewRotation,
    fillContainer,
    maxBitmapWidth = 1280,
    maxBitmapHeight = 720
  } = input

  const isRotated90or270 = previewRotation === 90 || previewRotation === 270

  // ── L2 effective camera ratio (post rotation) ───────────────────────────────────────────────
  // cropRatio is L2 in unrotated image space.  After a 90/270° CSS rotation the visual aspect
  // inverts: a 4:3 camera appears as 3:4 on screen.
  const displayCropRatio =
    cropRatio !== undefined && isRotated90or270 ? 1 / cropRatio : cropRatio

  // ── Step 1: size the preview box ────────────────────────────────────────────────────────────
  const { w: cw, h: ch } = container
  let previewW: number, previewH: number

  if (fillContainer) {
    if (displayCropRatio === undefined) {
      previewW = cw
      previewH = ch
    } else if (displayCropRatio >= 1) {
      const scale = Math.max(cw / displayCropRatio, ch)
      previewW = scale * displayCropRatio
      previewH = scale
    } else {
      const scale = Math.max(cw, ch * displayCropRatio)
      previewH = scale / displayCropRatio
      previewW = scale
    }
  } else if (displayCropRatio === undefined) {
    // Use real sensor ratio if available; fall back to container ratio.
    const fallback =
      sensorRatio !== undefined
        ? isRotated90or270
          ? 1 / sensorRatio
          : sensorRatio
        : cw / ch
    if (fallback >= 1) {
      previewW = Math.min(cw, ch * fallback)
      previewH = previewW / fallback
    } else {
      previewH = Math.min(ch, cw / fallback)
      previewW = previewH * fallback
    }
  } else if (displayCropRatio >= 1) {
    previewW = Math.min(cw, ch * displayCropRatio)
    previewH = previewW / displayCropRatio
  } else {
    previewH = Math.min(ch, cw / displayCropRatio)
    previewW = previewH * displayCropRatio
  }

  // ── Step 2: canvas backing store (pre-rotation) ─────────────────────────────────────────────
  // For 90/270° the canvas must be physically (previewH × previewW) so after the CSS rotate
  // its visual footprint becomes (previewW × previewH) = previewBoxPx.
  const canvasBackingW = isRotated90or270 ? previewH : previewW
  const canvasBackingH = isRotated90or270 ? previewW : previewH

  // ── Step 3: overlay mask ─────────────────────────────────────────────────────────────────────
  // effectiveOverlayRatio is L3 (frame slot) when provided; otherwise falls back to L2
  // so the comparison returns "no overlay".
  const overlayW = fillContainer ? cw : previewW
  const overlayH = fillContainer ? ch : previewH

  const effectiveOverlayRatio = frameRatio ?? displayCropRatio
  let overlayMask: PreviewLayoutOutput['overlayMask']

  if (
    !fillContainer &&
    displayCropRatio !== undefined &&
    effectiveOverlayRatio !== undefined &&
    Math.abs(effectiveOverlayRatio - displayCropRatio) > 0.01
  ) {
    let captureW = 0
    let captureH = 0
    if (effectiveOverlayRatio >= 1) {
      captureW = Math.min(overlayW, overlayH * effectiveOverlayRatio)
      captureH = captureW / effectiveOverlayRatio
    } else {
      captureH = Math.min(overlayH, overlayW / effectiveOverlayRatio)
      captureW = captureH * effectiveOverlayRatio
    }
    overlayMask = {
      x: (overlayW - captureW) / 2,
      y: (overlayH - captureH) / 2,
      w: captureW,
      h: captureH,
      containerW: overlayW,
      containerH: overlayH
    }
  }

  // ── Step 4: L2 crop rectangle in sensor pixel space ─────────────────────────────────────────
  let sx = 0
  let sy = 0
  let sw = sensorPixelW
  let sh = sensorPixelH

  if (cropRatio !== undefined && sensorPixelW > 0 && sensorPixelH > 0) {
    if (cropRatio >= 1) {
      // Landscape target: start from full height, compute width.
      sh = sensorPixelH
      sw = sensorPixelH * cropRatio
      if (sw > sensorPixelW) {
        sw = sensorPixelW
        sh = sensorPixelW / cropRatio
        sy = (sensorPixelH - sh) / 2
      } else {
        sx = (sensorPixelW - sw) / 2
      }
    } else {
      // Portrait target: start from full width, compute height.
      sw = sensorPixelW
      sh = sensorPixelW / cropRatio
      if (sh > sensorPixelH) {
        sh = sensorPixelH
        sw = sensorPixelH * cropRatio
        sx = (sensorPixelW - sw) / 2
      } else {
        sy = (sensorPixelH - sh) / 2
      }
    }
    sw = Math.max(1, Math.round(sw))
    sh = Math.max(1, Math.round(sh))
    sx = Math.max(0, Math.round(sx))
    sy = Math.max(0, Math.round(sy))
  }

  // ── Step 5: bitmap target size (fit-within, aspect-preserving) ──────────────────────────────
  const fitScale = Math.min(maxBitmapWidth / sw, maxBitmapHeight / sh, 1)
  const bitmapW = Math.max(2, Math.round((sw * fitScale) / 2) * 2)
  const bitmapH = Math.max(2, Math.round((sh * fitScale) / 2) * 2)

  return {
    previewBoxPx: { w: previewW, h: previewH },
    canvasBackingPx: { w: Math.round(canvasBackingW), h: Math.round(canvasBackingH) },
    overlayMask,
    cropRectSensor: { sx, sy, sw, sh },
    bitmapTargetPx: { w: bitmapW, h: bitmapH }
  }
}

/**
 * L2 + optional L3 (fillContainer + slot `ratio`) crop in **unrotated sensor pixel space**.
 * Matches Preview.tsx getBitmapParam / createImageBitmap source rect semantics.
 */
export function computeBitmapCropFromSensor(
  originalWidth: number,
  originalHeight: number,
  effectiveCameraRatio: number | undefined,
  fillContainer: boolean,
  ratio: number | undefined
): { sx: number; sy: number; cropWidth: number; cropHeight: number } {
  let cropWidth: number
  let cropHeight: number
  let sx = 0
  let sy = 0

  if (effectiveCameraRatio === undefined) {
    cropWidth = originalWidth
    cropHeight = originalHeight
    sx = 0
    sy = 0
  } else if (effectiveCameraRatio >= 1) {
    cropHeight = originalHeight
    cropWidth = originalHeight * effectiveCameraRatio
    if (cropWidth > originalWidth) {
      cropWidth = originalWidth
      cropHeight = originalWidth / effectiveCameraRatio
      sy = (originalHeight - cropHeight) / 2
    } else {
      sx = (originalWidth - cropWidth) / 2
    }
  } else {
    cropWidth = originalWidth
    cropHeight = originalWidth / effectiveCameraRatio
    if (cropHeight > originalHeight) {
      cropHeight = originalHeight
      cropWidth = originalHeight * effectiveCameraRatio
      sx = (originalWidth - cropWidth) / 2
    } else {
      sy = (originalHeight - cropHeight) / 2
    }
  }

  cropWidth = Math.max(1, Math.round(cropWidth))
  cropHeight = Math.max(1, Math.round(cropHeight))
  sx = Math.max(0, Math.round(sx))
  sy = Math.max(0, Math.round(sy))

  if (
    fillContainer &&
    ratio !== undefined &&
    typeof ratio === 'number' &&
    Number.isFinite(ratio) &&
    ratio > 0
  ) {
    const l2w = cropWidth
    const l2h = cropHeight
    const l2Aspect = l2w / l2h
    if (Math.abs(l2Aspect - ratio) > 0.001) {
      let l3w = l2w
      let l3h = l2h
      if (l2Aspect > ratio) {
        l3w = Math.max(1, Math.round(l2h * ratio))
        l3h = l2h
      } else {
        l3h = Math.max(1, Math.round(l2w / ratio))
        l3w = l2w
      }
      const dx = Math.floor((l2w - l3w) / 2)
      const dy = Math.floor((l2h - l3h) / 2)
      sx += dx
      sy += dy
      cropWidth = l3w
      cropHeight = l3h
    }
  }

  return { sx, sy, cropWidth, cropHeight }
}

/**
 * Explicit crop rectangle in **unrotated sensor pixel space** from a normalized rect
 * (x/y/w/h ∈ [0,1]). Unlike {@link computeBitmapCropFromSensor} — which only ever centers a
 * crop of a given aspect — this honors an arbitrary offset, which is what the Bahelakotak
 * custom-box crop needs (the two cameras never frame the physical box windows identically).
 * Result matches `createImageBitmap(blob, sx, sy, cropWidth, cropHeight)` semantics.
 */
export function computeExplicitCropFromSensor(
  originalWidth: number,
  originalHeight: number,
  rect: { x: number; y: number; w: number; h: number }
): { sx: number; sy: number; cropWidth: number; cropHeight: number } {
  const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
  const x = clamp01(rect.x)
  const y = clamp01(rect.y)
  const w = clamp01(rect.w)
  const h = clamp01(rect.h)

  let sx = Math.round(x * originalWidth)
  let sy = Math.round(y * originalHeight)
  let cropWidth = Math.max(1, Math.round(w * originalWidth))
  let cropHeight = Math.max(1, Math.round(h * originalHeight))

  // Keep the source rect fully inside the sensor even after rounding.
  if (sx + cropWidth > originalWidth) sx = Math.max(0, originalWidth - cropWidth)
  if (sy + cropHeight > originalHeight) sy = Math.max(0, originalHeight - cropHeight)
  cropWidth = Math.min(cropWidth, originalWidth - sx)
  cropHeight = Math.min(cropHeight, originalHeight - sy)

  return { sx, sy, cropWidth, cropHeight }
}
